import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { ThemedSection, useTheme } from '../contexts/ThemeContext';
import { Phone, Heart, Clock, Calendar, Droplet, Check } from 'lucide-react';
import Reveal from '../components/Reveal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const BloodBank = () => {
  const { language } = useLanguage();
  const { bloodBankThemeKey } = useTheme();
  const [components, setComponents] = useState(null);
  const [bloodSettings, setBloodSettings] = useState(null);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [componentsRes, settingsRes, generalRes, eventsRes] = await Promise.all([
        axios.get(`${API}/blood-bank/components`),
        axios.get(`${API}/blood-bank/settings`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/blood-bank/events`),
      ]);
      setComponents(componentsRes.data);
      setBloodSettings(settingsRes.data);
      setGeneralSettings(generalRes.data);
      setEvents(eventsRes.data || []);
    } catch {
      setComponents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const displayComponents = (components && components.length > 0)
    ? components
    : DEFAULT_BLOOD_GROUPS.map((group, i) => ({ id: i, name_en: group, name_hi: group }));

  const phoneNumber = bloodSettings?.phone || generalSettings?.phone;

  return (
    <ThemedSection themeKey={bloodBankThemeKey} className="min-h-screen bg-white font-body">
      <div data-testid="blood-bank-page">
        <Navbar />

        {/* Hero — themed banner */}
        <PageHero
          logoUrl={generalSettings?.blood_bank_logo_url}
          title={language === 'en'
            ? (generalSettings?.blood_bank_title_en || 'Blood Centre')
            : (generalSettings?.blood_bank_title_hi || 'रक्त केंद्र')}
          italicNote={language === 'en'
            ? (generalSettings?.blood_bank_subtitle_en || '(A Unit of SKB Aarogyam Super Speciality Healthcare)')
            : (generalSettings?.blood_bank_subtitle_hi || '(एसकेबी आरोग्यम सुपर स्पेशियलिटी हेल्थकेयर की एक इकाई)')}
          subtitle={bloodSettings
            ? (language === 'en' ? bloodSettings.description_en : bloodSettings.description_hi)
            : (language === 'en'
                ? 'Safe and reliable blood banking services available 24/7'
                : '24/7 उपलब्ध सुरक्षित और विश्वसनीय रक्त बैंक सेवाएं')}
          testId="blood-bank-hero"
        />

        {/* Blood Components */}
        <div className="py-20 md:py-28" data-testid="blood-components-section">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
                {language === 'en' ? 'Available now' : 'अभी उपलब्ध'}
              </p>
              <h2 className="font-heading font-light text-3xl sm:text-4xl text-brand-navy uppercase tracking-ultra-wide mb-4">
                {language === 'en' ? 'Blood Components' : 'रक्त घटक'}
              </h2>
              <div className="w-12 h-px bg-brand-teal mx-auto"></div>
            </div>
            {loading ? (
              <div className="text-center text-neutral-400 py-12">Loading...</div>
            ) : (
              <div className="flex flex-col gap-3 sm:gap-4" data-testid="blood-groups-grid">
                {displayComponents.map((component, index) => {
                  const displayText = language === 'en' ? component.name_en : component.name_hi;
                  return (
                    <Reveal
                      key={component.id || index}
                      delay={index * 70}
                      className="group relative flex items-center justify-between gap-4 sm:gap-6 px-5 sm:px-8 py-5 sm:py-6 bg-white border border-brand-teal-tint hover:border-brand-teal transition-all duration-300 hover:shadow-[0_8px_30px_rgba(15,118,110,0.08)] hover:-translate-y-0.5 overflow-hidden"
                      data-testid={`blood-group-${index}`}
                    >
                      {/* Left accent bar */}
                      <span className="absolute left-0 top-0 bottom-0 w-1 bg-brand-emergency/0 group-hover:bg-brand-emergency transition-colors duration-300"></span>

                      <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                        <div className="flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-brand-teal-tint/60 group-hover:bg-brand-emergency/10 transition-colors duration-300 shrink-0">
                          <Droplet
                            className="h-5 w-5 sm:h-6 sm:w-6 text-brand-teal group-hover:text-brand-emergency transition-colors duration-300"
                            fill="currentColor"
                            strokeWidth={1.5}
                          />
                        </div>
                        <div className="min-w-0">
                          <span className="block font-heading font-light text-2xl sm:text-3xl text-brand-navy tracking-wide leading-none">
                            {displayText}
                          </span>
                          <span className="hidden sm:block text-[10px] uppercase tracking-ultra-wide text-neutral-400 font-body mt-2">
                            {language === 'en' ? 'Blood Group' : 'रक्त समूह'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                        <span className="text-brand-teal font-heading font-light text-lg sm:text-xl opacity-0 group-hover:opacity-100 translate-x-[-6px] group-hover:translate-x-0 transition-all duration-300">
                          →
                        </span>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Blood Bank Facility Features (no heading) */}
        {bloodSettings && (
          (bloodSettings.feature1_en || bloodSettings.feature2_en || bloodSettings.feature3_en) && (
            <div className="border-t border-brand-teal-tint/60" data-testid="blood-bank-features-section">
              <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6">
                  {[
                    { en: bloodSettings.feature1_en, hi: bloodSettings.feature1_hi },
                    { en: bloodSettings.feature2_en, hi: bloodSettings.feature2_hi },
                    { en: bloodSettings.feature3_en, hi: bloodSettings.feature3_hi },
                  ].map((f, i) => {
                    const text = language === 'en' ? f.en : (f.hi || f.en);
                    if (!text) return null;
                    return (
                      <Reveal
                        key={`feature-${i}-${(f.en || '').slice(0, 16)}`}
                        delay={i * 150}
                        className="flex items-center gap-4 sm:gap-5 text-left"
                        data-testid={`blood-bank-feature-${i}`}
                      >
                        <span className="flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-brand-teal text-white shrink-0 shadow-sm">
                          <Check className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                        </span>
                        <p className="font-heading font-light text-base sm:text-lg text-brand-navy leading-snug tracking-wide">
                          {text}
                        </p>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </div>
          )
        )}

        {/* Specialized Collection Systems — compact, no big banner */}
        {bloodSettings && (bloodSettings.collection1_title_en || bloodSettings.collection2_title_en) && (
          <div className="border-t border-brand-teal-tint/60" data-testid="collection-systems-section">
            <div className="max-w-5xl mx-auto px-6 py-12 md:py-14">
              <h3 className="font-heading font-light text-lg sm:text-xl text-brand-navy tracking-wide mb-6">
                {language === 'en'
                  ? (bloodSettings.collection_heading_en || 'Specialised Collection Equipments')
                  : (bloodSettings.collection_heading_hi || bloodSettings.collection_heading_en || 'विशिष्ट संग्रहण उपकरण')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {[
                  {
                    title: language === 'en' ? bloodSettings.collection1_title_en : (bloodSettings.collection1_title_hi || bloodSettings.collection1_title_en),
                    tag: language === 'en' ? bloodSettings.collection1_tag_en : (bloodSettings.collection1_tag_hi || bloodSettings.collection1_tag_en),
                  },
                  {
                    title: language === 'en' ? bloodSettings.collection2_title_en : (bloodSettings.collection2_title_hi || bloodSettings.collection2_title_en),
                    tag: language === 'en' ? bloodSettings.collection2_tag_en : (bloodSettings.collection2_tag_hi || bloodSettings.collection2_tag_en),
                  },
                ].map((item, i) => {
                  if (!item.title) return null;
                  return (
                    <div
                      key={`collection-${i}-${(item.title || '').slice(0, 16)}`}
                      className="flex items-center gap-3 px-4 py-3 bg-brand-teal-tint/40 hover:bg-brand-teal-tint/70 border-l-2 border-brand-teal transition-colors duration-300"
                      data-testid={`collection-system-${i}`}
                    >
                      <Droplet
                        className="h-4 w-4 text-brand-teal shrink-0"
                        fill="currentColor"
                        strokeWidth={1.5}
                      />
                      <span className="font-heading font-light text-base text-brand-navy">
                        {item.title}
                      </span>
                      {item.tag && (
                        <span className="ml-auto text-[10px] uppercase tracking-ultra-wide text-brand-teal font-body whitespace-nowrap">
                          {item.tag}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Events Section */}
        {events && events.length > 0 && (
          <div className="bg-brand-teal-tint py-20 md:py-28" data-testid="blood-bank-events-section">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-14">
                <p className="text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
                  {language === 'en' ? 'Community Drives' : 'सामुदायिक अभियान'}
                </p>
                <h2 className="font-heading font-light text-3xl sm:text-4xl text-brand-navy uppercase tracking-ultra-wide mb-4">
                  {language === 'en' ? 'Blood Donation Events' : 'रक्तदान कार्यक्रम'}
                </h2>
                <div className="w-12 h-px bg-brand-teal mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {events.map((event, index) => {
                  const title = language === 'en' ? event.title_en : event.title_hi;
                  const description = language === 'en' ? event.description_en : event.description_hi;
                  return (
                    <article
                      key={event.id}
                      className="bg-white overflow-hidden hover:shadow-lg transition-all duration-500 group"
                      data-testid={`blood-event-card-${index}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-brand-teal-tint">
                        <img
                          src={event.image_url}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6">
                        {event.event_date && (
                          <div className="flex items-center gap-2 text-xs uppercase tracking-ultra-wide text-brand-teal font-body mb-3">
                            <Calendar className="h-3 w-3" />
                            <time dateTime={event.event_date}>{new Date(event.event_date).toLocaleDateString(language === 'en' ? 'en-IN' : 'hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</time>
                          </div>
                        )}
                        <h3 className="font-heading font-light text-xl text-brand-navy mb-3 leading-snug">
                          {title}
                        </h3>
                        {description && (
                          <p className="font-body font-light text-sm text-neutral-600 leading-relaxed line-clamp-3">
                            {description}
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Emergency Contact CTA */}
        <div className="bg-brand-navy py-20 md:py-24 text-center" data-testid="blood-bank-emergency">
          <div className="max-w-3xl mx-auto px-6">
            <Heart className="h-7 w-7 text-brand-emergency mx-auto mb-6" fill="currentColor" />
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-white uppercase tracking-ultra-wide mb-4">
              {language === 'en' ? 'Need Blood?' : 'रक्त की आवश्यकता है?'}
            </h2>
            <div className="w-12 h-px bg-brand-emergency mx-auto mb-6"></div>
            <p className="text-white/70 font-body font-light text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              {language === 'en'
                ? 'Our blood bank is operational 24/7 for emergency requirements. Reach us anytime.'
                : 'आपातकालीन आवश्यकताओं के लिए हमारा रक्त बैंक 24/7 चालू है। किसी भी समय हमसे संपर्क करें।'}
            </p>
            {phoneNumber ? (
              <a
                href={`tel:${phoneNumber}`}
                className="inline-flex items-center gap-3 px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark transition-colors duration-300"
                data-testid="blood-bank-contact-btn"
              >
                <Phone className="h-4 w-4" />
                {phoneNumber}
              </a>
            ) : (
              <a
                href="/contact"
                className="inline-flex items-center gap-3 px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark transition-colors duration-300"
                data-testid="blood-bank-contact-btn"
              >
                <Phone className="h-4 w-4" />
                {language === 'en' ? 'Contact Now' : 'अभी संपर्क करें'}
              </a>
            )}
            <p className="text-xs uppercase tracking-ultra-wide text-white/40 font-body mt-8 flex items-center justify-center gap-2">
              <Clock className="h-3 w-3" />
              {language === 'en' ? '24 / 7 · Open Always' : '24 / 7 · हमेशा खुला'}
            </p>
          </div>
        </div>

        <Footer />
      </div>
    </ThemedSection>
  );
};

export default BloodBank;
